import { BaseApiService } from "./baseApiService";
import { AUTH_URLS } from "../utils/authUtils";

export interface UserProfile {
  user: {
    id: string;
    name: string;
    email: string;
    locale?: string;
    timezone?: string;
    userType?: string;
  };
  organization: {
    id: string;
    name: string;
    subdomain: string;
    locale?: string;
  };
  profileUrl: string;
  isAuthenticated: boolean;
}

export interface UserProfileApiResponse {
  success: boolean;
  data?: UserProfile;
  error?: string;
  errorCode?: string;
}

export class UserApiService extends BaseApiService {
  private static instance: UserApiService | null = null;

  public static getInstance(): UserApiService {
    if (!UserApiService.instance) {
      UserApiService.instance = new UserApiService();
    }
    return UserApiService.instance;
  }

  /**
   * Get current user profile information
   * @returns Promise resolving to user profile data
   */
  async getUserProfile(): Promise<UserProfileApiResponse> {
    try {
      // Use static method that properly handles smart API routing
      // This will automatically route to /api/v1/private/user/profile for authenticated users
      // or /api/v1/public/user/profile for unauthenticated users
      const response = await BaseApiService.get({
        url: "/user/profile",
        responseDefaults: {},
      });

      // Handle both old structure (response.data) and new structure (direct response)
      const profileData = response.data || response;

      if (response.success && profileData) {
        // Check if the response has the expected UserProfile structure
        const hasValidStructure =
          profileData &&
          typeof profileData === "object" &&
          "user" in profileData &&
          "organization" in profileData &&
          "isAuthenticated" in profileData;

        if (hasValidStructure) {
          return {
            success: true,
            data: profileData as UserProfile,
          };
        } else {
          return {
            success: false,
            error: "Invalid profile data structure",
          };
        }
      } else {
        return {
          success: false,
          error: response.error || "Failed to fetch user profile",
        };
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch user profile",
      };
    }
  }

  /**
   * Get JWT HighBond client for login/logout operations
   */
  getJwtHighbondClient() {
    // No longer used; keep method for backward compatibility
    return null;
  }

  /**
   * Logout functionality - redirects to HighBond logout endpoint
   */
  logout(): void {
    // Redirect to logout page (let HighBond handle token clearing and redirect)
    if (typeof window !== "undefined") {
      window.location.href = AUTH_URLS.logout();
    }
  }
}

// Export singleton instance
export const userApiService = UserApiService.getInstance();
